import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api, { formatApiError } from "../../lib/api";
import NotificationStatusBadge from "./NotificationStatusBadge";

const EVENT_LABEL = {
  billing_past_due: "Billing past due",
  billing_unpaid: "Billing unpaid",
  ack_received: "Owner acknowledged",
  ack_requested: "Reminder sent",
};

/**
 * Phase 8 — small "3 most recent notifications" feed for SubscriptionDetail
 * and DiagnosticReportPage. Filterable by either related_subscription_id or
 * related_report_id (pass exactly one).
 */
export default function NotificationsMiniFeed({ subscriptionId, reportId }) {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!subscriptionId && !reportId) return;
    setErr("");
    try {
      const params = new URLSearchParams({ limit: "3" });
      if (subscriptionId) params.set("related_subscription_id", subscriptionId);
      if (reportId) params.set("related_report_id", reportId);
      const r = await api.get(`/notifications/?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [subscriptionId, reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const filterQs = subscriptionId
    ? `?related_subscription_id=${subscriptionId}`
    : `?related_report_id=${reportId}`;

  return (
    <section
      data-testid="notif-mini-feed"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-heading text-sm font-semibold text-slate-900">
          Recent notifications
        </div>
        <Link
          to={`/notifications${filterQs}`}
          className="text-[11px] font-semibold text-slate-700 hover:text-slate-900"
          data-testid="notif-mini-feed-view-all"
        >
          View all →
        </Link>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
        Last 3 outbound emails for this {subscriptionId ? "subscription" : "report"}
      </div>

      {err && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 mb-3">
          {err}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-md bg-slate-50 border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500">
          No notifications yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((n) => (
            <li
              key={n.id}
              data-testid={`notif-mini-row-${n.id}`}
              className="flex items-start justify-between gap-3 text-xs"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                  <span className="font-semibold text-slate-900">
                    {EVENT_LABEL[n.event_type] || n.event_type}
                  </span>
                  <NotificationStatusBadge status={n.status} />
                </div>
                <div className="text-slate-600 truncate">{n.subject}</div>
                <div className="text-[10px] text-slate-500">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <Link
                to={`/notifications/${n.id}`}
                className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 flex-none"
              >
                Open
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
